import { supabase } from '../lib/supabase';
import { authService } from './authService';

// Anexo D.4.7 — Unificación de las 3 formas de eliminación de cuenta
// (antiguamente dispersas en privacyService.deleteAccount, authService.deleteAccount
// y routes/api/delete-account.ts) en una sola implementación canónica.
//
// Cualquier interfaz (SettingsModal, API REST o scripts de soporte) debe invocar
// esta función. Garantiza:
//   1. Identificación del usuario autenticado (RPC + service_role permitido).
//   2. Notificación de "último aviso" antes de la baja (aunque se borre el perfil
//      inmediatamente después, el registro queda en la tabla notifications histórico
//      si el DBA lo recupera).
//   3. Ejecución del RPC `public.delete_user_cascade(UUID)` único (migración
//      20260813120000_unified_delete_user_cascade.sql) responsable de borrar TODO
//      el rastro: posts, comentarios, likes, media, mensajes, ordenes, ads, etc.
//   4. Cierre de sesión en cliente Supabase (tanto JWT como cookie) para impedir
//      que la sesión siga siendo válida si el tarda en revocarse.
//   5. Limpieza de datos locales: tokens, preferencias, impersonation, etc.

const clearLocalState = () => {
  if (typeof window === 'undefined') return;
  try {
    const keysToRemove = [
      'originalAdminToken',
      'expo_push_token',
      'soartistic-impersonation',
      'privacy-settings',
      'notification-settings',
    ];
    keysToRemove.forEach((k) => window.localStorage.removeItem(k));
    window.sessionStorage.clear();
  } catch {
    // noop
  }
};

export const accountDeletionService = {
  async requestAccountDeletion(): Promise<{ success: boolean; message: string }> {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) throw authError;
    if (!user) throw new Error('User not authenticated');

    const userId = user.id;

    // Notificación final — best effort, no bloquea la baja si falla.
    try {
      const { error: notifError } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type: 'system',
          title: 'Cuenta eliminada',
          body: 'Tu cuenta de SoArtistic ha sido eliminada. Si esto es un error, contacta con soporte en un plazo máximo de 30 días.',
          is_read: false,
        });
      if (notifError) {
        console.warn('[accountDeletionService] no se pudo registrar notificación final:', notifError);
      }
    } catch {
      // ignore
    }

    const { error: rpcError } = await supabase.rpc('delete_user_cascade', {
      target_user_id: userId,
    });

    if (rpcError) throw rpcError;

    try {
      await authService.signOut();
    } catch {
      // En caso de que Supabase haya revocado la sesión, no propagar error.
    }

    clearLocalState();

    return {
      success: true,
      message: 'Cuenta y datos asociados eliminados correctamente.',
    };
  },
};
